import { JsonPipe, NgTemplateOutlet } from '@angular/common';
import { Component, computed, contentChild, effect, inject, input, OnInit, output, signal, TemplateRef } from '@angular/core';
import { UtilityService } from '@eforall/common';
import { SelectEvent, TabStripModule } from '@progress/kendo-angular-layout';
import { DashboardLayoutDimensions, DashboardLayoutService, DashboardMenuService, PAGE_TABS_LEFT_AND_RIGHT_PADDING, PAGE_TABS_TAB_HEIGHT, PAGE_TABS_TOP_AND_BOTTOM_PADDING, TAB1_VIEW_DROPLIST_HEIGHT, TABS_COLUMN_GAP, TOOLBAR_HEIGHT } from '../../services';
import { DEFAULT_CONFIG } from './page-tabs-config';
import { PageTabsLayout } from './page-tabs-layout';
import { PageTabsLayoutPageService } from './page-tabs-layout-page.service';
import { PageTab1Directive, PageTab2Directive, PageTab3Directive, PageTab4Directive, PageTab5Directive, PageTab6Directive, PageTab7Directive } from './page-tabs.directives';

@Component({
	selector: 'page-tabs',
	standalone: true,
	imports: [
		TabStripModule,
		JsonPipe,
		NgTemplateOutlet
	],
	templateUrl: './page-tabs.widget.html',
	styleUrls: ['page-tabs.widget.scss'],
})
export class PageTabsWidget implements OnInit {

	private layoutService = inject(DashboardLayoutService);
	private menu = inject(DashboardMenuService);
	private util = inject(UtilityService);

	public readonly tabNames = input.required<string[]>();
	public readonly pageService = input.required<PageTabsLayoutPageService<number, number>>();
	public readonly config = input(DEFAULT_CONFIG);
	public layoutChange = output<PageTabsLayout>();

	public pageTab1 = contentChild.required(PageTab1Directive);
	public pageTab2 = contentChild(PageTab2Directive);
	public pageTab3 = contentChild(PageTab3Directive);
	public pageTab4 = contentChild(PageTab4Directive);
	public pageTab5 = contentChild(PageTab5Directive);
	public pageTab6 = contentChild(PageTab6Directive);
	public pageTab7 = contentChild(PageTab7Directive);


	public mergedConfig = computed(() => ({ ...DEFAULT_CONFIG, ...(this.config() || {}) }));
	public selected = computed(() => (this.pageService().selectedTab()));


	public readonly gap = 13; // number of pixels between expanded first tab and page tabs
	public gridTemplateColumns = signal('1fr');
	public contentHeight = 500;

	private _tab1ShouldExpand = signal(true);
	public tab1ShouldExpand = this._tab1ShouldExpand.asReadonly();



	private _tab1ForceCollapse = signal(false);

	public tab1Expanded = computed(() => this._tab1ShouldExpand() && !this._tab1ForceCollapse());

	private _leftTabIndex = signal<number>(0);
	public leftTabIndex = this._leftTabIndex.asReadonly();
	private _rightTabIndex = signal<number>(1);
	public rightTabIndex = this._rightTabIndex.asReadonly();

	extraTabs: { name: string, template: TemplateRef<unknown> | undefined }[] = [];


	constructor() {
		effect(() => {
			const dimensions = this.layoutService.dimensions(); // Get the current value
			this.applyDimensions(dimensions);
			this.buildPageTabsLayout();
		});
	}

	async ngOnInit() {
		// Validate parameters
		if (this.tabNames().length < 2) throw new Error(`Attribute tabNames must be at least 2 tab names.`);
		if (this.tabNames().length > 7) this.util.log.errorMessage(`Attribute tabNames exceeds the maximum of 7 tab names.`);

		// Initialize selections
		this._leftTabIndex.set(0);
		this._rightTabIndex.set(1);

		// Setup all the tabs beyond the main one
		this.extraTabs = this.getExtraTabs();

		// Select the first tab
		this.selectTab(0);

		// Handle layout changes
		const dimensions = this.layoutService.dimensions();
		this.applyDimensions(dimensions);
		this.buildPageTabsLayout();

		// Manually trigger updates when selected tab or tab1Expanded changes
		this.updateLayout();
	}

	private getExtraTabs(): { name: string, template: TemplateRef<unknown> | undefined }[] {
		if ((this.tabNames()?.length ?? 0) < 2) return [];

		return this.tabNames().slice(1).map((name: string, i: number) => {
			let template: TemplateRef<unknown> | undefined = undefined;

			if (i == 0) template = this.pageTab2()?.tpl;
			if (i == 1) template = this.pageTab3()?.tpl;
			if (i == 2) template = this.pageTab4()?.tpl;
			if (i == 3) template = this.pageTab5()?.tpl;
			if (i == 4) template = this.pageTab6()?.tpl;
			if (i == 5) template = this.pageTab7()?.tpl;

			return { name, template };
		});
	}

	selectTab(tabId: number) {
		const expanded = this._tab1ShouldExpand() && !this._tab1ForceCollapse();

		if (expanded) {
			if (this.leftTabIndex() !== 0) this._leftTabIndex.set(0);
			if (tabId < 1) tabId = 1;  // Skip the main tab because it's on the left side
			if (this.rightTabIndex() !== tabId) this._rightTabIndex.set(tabId);
		} else {
			if (this.leftTabIndex() !== tabId) this._leftTabIndex.set(tabId);
			if (this.rightTabIndex() !== tabId) this._rightTabIndex.set(tabId);
		}
	}

	onTabSelect(side: 'left' | 'right', tab: SelectEvent) {
		const tabNameIndex = this.tabNames().indexOf(tab.title);
		this.pageService().selectTab(tabNameIndex + 1);
		this.selectTab(tabNameIndex);
		this.updateLayout();
	}

	async applyDimensions(dimensions: DashboardLayoutDimensions): Promise<void> {
		const breakpoint = this.mergedConfig().breakpoint! + this.gap + this.mergedConfig().pageTabsWidth!;

		const shouldExpand = dimensions.contentWidth >= breakpoint;

		if (this._tab1ShouldExpand() !== shouldExpand) {
			this._tab1ShouldExpand.set(shouldExpand);
		}
		this.updateLayout();
	}

	buildPageTabsLayout() {
		let dimensions = this.layoutService.dimensions();

		const forceCollapse = this._tab1ForceCollapse();
		const shouldExpand = this._tab1ShouldExpand();
		const tab1Expanded = shouldExpand && !forceCollapse;

		let pageTabContentHeight = dimensions.contentHeight - PAGE_TABS_TAB_HEIGHT - 2 * PAGE_TABS_TOP_AND_BOTTOM_PADDING;


		if (this.mergedConfig().hasToolbar) {
			dimensions = {
				...dimensions,
				contentHeight: dimensions.contentHeight - TOOLBAR_HEIGHT,
			};
			pageTabContentHeight = pageTabContentHeight - TOOLBAR_HEIGHT;
		}

		this.contentHeight = dimensions.contentHeight;

		const pageTabContentWidth = dimensions.contentWidth - PAGE_TABS_LEFT_AND_RIGHT_PADDING;
		const tabContentWidth = this.mergedConfig().pageTabsWidth! - PAGE_TABS_LEFT_AND_RIGHT_PADDING;
		const tab1ExpandedContentWidth = dimensions.contentWidth - this.mergedConfig().pageTabsWidth! - TABS_COLUMN_GAP - PAGE_TABS_LEFT_AND_RIGHT_PADDING;

		const layout: PageTabsLayout = {
			tab1Expanded: tab1Expanded,
			tab1ContentWidth: tab1Expanded ? tab1ExpandedContentWidth : pageTabContentWidth,
			tab1ContentHeight: pageTabContentHeight,
			tab1ViewDroplistHeight: TAB1_VIEW_DROPLIST_HEIGHT,
			tabContentWidth: tab1Expanded ? tabContentWidth : pageTabContentWidth,
			tabContentHeight: pageTabContentHeight,
			dimensions,
		};

		this.layoutChange.emit(layout);
	}

	toggleForce() {
		this.menu.setMenuCollapsed(true);
		this._tab1ForceCollapse.set(!this._tab1ForceCollapse());
		this.updateLayout();
	}

	private updateLayout() {
		const selectedTab = this.selected();
		const expanded = this.tab1Expanded();

		const tabId = selectedTab ? selectedTab - 1 : 0;

		this.selectTab(tabId);
		this.gridTemplateColumns.set('1fr' + (expanded ? ` ${this.mergedConfig().pageTabsWidth}px` : ''));

		this.buildPageTabsLayout();
	}
}